import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { getWacIntegrationUrl } from '@/utils/urls.ts';

import { type ChoiceValue } from './loom-choice-variant-a';
import { type LoomEdition } from './use-twc-price';

// Continue URLs for the opt-in CTAs. The TWC edition mirrors the price
// comparison (Business + AI → Premium, otherwise Standard). `loomIntent=true`
// lets the confirmation page route back into Loom onboarding (STARB-4196).
const twcSignupPath = (twcEdition: TwcSignupEdition): string =>
	`/try/cloud/signup?bundle=teamwork-collection&edition=${twcEdition}&loomIntent=true`;

// The Loom-only CTA mirrors the control cohort: fall through to the standard
// signup page with the original query string (edition, application, campaign
// params) preserved, rather than a hard-coded path.
const loomSignupPath = (search: string): string => `/try/cloud/signup${search}`;

type TwcSignupEdition = 'premium' | 'standard';

// Loom edition → provisioned TWC edition (mirrors use-twc-price.ts).
const LOOM_EDITION_TO_TWC_SIGNUP_EDITION: Record<LoomEdition, TwcSignupEdition> = {
	free: 'standard',
	starter: 'standard',
	business: 'standard',
	standard: 'standard',
	premium: 'premium',
	enterprise: 'premium',
};

/**
 * Returns a navigate-to-choice callback. URLs are absolute WAC URLs
 * (`useLocalizedHref` + getWacIntegrationUrl) for the top-level signup redirect.
 */
export const useChoiceRedirect = (
	edition: LoomEdition = 'premium',
	search = '',
): ((choice: ChoiceValue) => void) => {
	const twcEdition = LOOM_EDITION_TO_TWC_SIGNUP_EDITION[edition];
	// useLocalizedHref is a hook, so resolve both editions and pick at nav time.
	const twcPremiumHref = useLocalizedHref(twcSignupPath('premium'));
	const twcStandardHref = useLocalizedHref(twcSignupPath('standard'));
	const loomHref = useLocalizedHref(loomSignupPath(search));

	return (choice: ChoiceValue) => {
		if (choice !== 'teamwork-collection') {
			window.location.assign(`${getWacIntegrationUrl()}${loomHref}`);
			return;
		}
		const twcHref = twcEdition === 'standard' ? twcStandardHref : twcPremiumHref;
		window.location.assign(`${getWacIntegrationUrl()}${twcHref}`);
	};
};
